import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { IoIosCloseCircle } from "react-icons/io";
import { FiUploadCloud, FiDownload } from "react-icons/fi";
import axios from 'axios';
import { Base_Url, csvuplode } from '../../../config/config';
import { toast } from 'react-toastify';
import { useMyContext } from '../../../context/MyContext';
import ConfirmBulkOrder from './ConfirmBulkOrder';
import Spinner from '../../spinner/spinner';
import Papa from 'papaparse';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FileText,
    Sparkles,
    X,
    CheckCircle,
    Eye,
    AlertCircle,
    Download,
} from "lucide-react";


const UploadBulk = ({ isopen, onClose }) => {
    const [file, setFile] = useState(null);
    const { isDarkMode } = useMyContext();
    const [reqBody, setReqBody] = useState([])
    const [loading, setloading] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [csvParsed, setCsvParsed] = useState(false);
    const [previewData, setPreviewData] = useState([]);

    const navigate = useNavigate();

    const handleViewClick = () => {
        setIsPopupOpen(true);
    };
    const handleClose = () => {
        setIsPopupOpen(false);
    };


    const handlePreviewClick = () => {
        if (previewData.length > 0) {
            setShowPreview(true);
        }
    };

    const closePreviewModal = () => {
        setShowPreview(false);
    };

    const handleCloseBulk = () => {
        onClose();
        setFile(null);
        setPreviewData([]);
        setCsvParsed(false);
    };

    const parseCSV = (csvText) => {
        const parsed = Papa.parse(csvText, {
            header: true,
            skipEmptyLines: true,
            dynamicTyping: true
        });
        return parsed.data;
    };


    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const csvData = parseCSV(e.target.result);
                    setPreviewData(csvData);
                    setCsvParsed(true);
                } catch (error) {
                    toast.error('Error parsing CSV file');
                    setCsvParsed(false);
                }
            };
            reader.readAsText(selectedFile);
        } else {
            toast.error('Please select a valid CSV file.');
            event.target.value = null;
        }
    };


    const handleSubmit = async (event) => {
        event.preventDefault();
        setloading(true);
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const shipment_type = "forword";

        if (file) {
            const formData = new FormData();
            formData.append('csv_file', file);
            formData.append("shipment_type", shipment_type);

            try {
                const response = await axios.post(Base_Url + csvuplode, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'token': token,
                        'cus_id': cus_id,
                    },
                });

                event.target.reset();

                if (response.data.status === "success") {
                    toast.success(response.data.message || "File uploaded successfully!");
                    setReqBody(response.data);
                    navigate("/shipments/all");
                    handleClose();
                } else {
                    if (response.data.errors && Array.isArray(response.data.errors)) {
                        response.data.errors.forEach((err) => toast.error(err));
                    } else {
                        toast.error(response.data.message || "File upload failed.");
                    }
                }
            } catch (error) {
                toast.error("Error uploading file: " + error.message);
            } finally {
                setloading(false);
            }
        }
    };


    const PreviewModal = () => {
        if (!previewData || previewData.length === 0) return null;

        const headers = Object.keys(previewData[0]);

        return (
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 dark:bg-black/80 backdrop-blur-sm p-4"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="bg-white dark:bg-[#0b0b14] rounded-2xl shadow-2xl dark:shadow-[0_20px_60px_rgba(79,70,229,0.5)] w-11/12 max-w-5xl max-h-[85vh] overflow-hidden border border-gray-200 dark:border-indigo-500/40"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600">
                            <div className="flex items-center gap-2.5">
                                <FileText className="w-4.5 h-4.5 text-white" />
                                <div>
                                    <h2 className="text-sm font-bold text-white leading-tight">Bulk Upload Preview</h2>
                                    <p className="text-purple-100 text-xs leading-tight">{previewData.length} records • {headers.length} columns</p>
                                </div>
                            </div>
                            <button
                                onClick={closePreviewModal}
                                className="p-1.5 bg-white/15 hover:bg-white/25 rounded-full transition-colors text-white"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Table */}
                        <div className="overflow-auto max-h-[65vh] p-3">
                            <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-indigo-500/30">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="sticky top-0 bg-indigo-50 dark:bg-[#131326]">
                                            <tr>
                                                {headers.map((header, index) => (
                                                    <th
                                                        key={index}
                                                        className="px-3 py-2 text-left font-semibold text-indigo-700 dark:text-indigo-200 text-xs uppercase tracking-wide border-r border-gray-200 dark:border-indigo-500/30 last:border-r-0"
                                                    >
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewData.slice(0, 50).map((row, rowIndex) => (
                                                <tr
                                                    key={rowIndex}
                                                    className={`${rowIndex % 2 === 0
                                                        ? 'bg-white dark:bg-[#0b0b14]'
                                                        : 'bg-gray-50 dark:bg-[#0f0f1c]'
                                                        } hover:bg-indigo-50 dark:hover:bg-indigo-500/15 transition-colors`}
                                                >
                                                    {headers.map((header, colIndex) => (
                                                        <td
                                                            key={colIndex}
                                                            className="px-3 py-2 text-gray-700 dark:text-gray-100 border-r border-gray-100 dark:border-indigo-500/15 last:border-r-0"
                                                        >
                                                            <div className="truncate max-w-xs" title={row[header]}>
                                                                {row[header]}
                                                            </div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {previewData.length > 50 && (
                                <p className="text-center text-xs text-amber-600 dark:text-amber-400 mt-2">
                                    Showing first 50 of {previewData.length} rows
                                </p>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex justify-between items-center px-5 py-3 border-t border-gray-100 dark:border-indigo-500/20">
                            <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-indigo-200/80">
                                <CheckCircle className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                                Data loaded successfully
                            </span>
                            <button
                                onClick={closePreviewModal}
                                className="px-4 py-1.5 bg-slate-700 dark:bg-gradient-to-r dark:from-indigo-600 dark:to-purple-600 dark:hover:from-indigo-500 dark:hover:to-purple-500 dark:shadow-md dark:shadow-indigo-900/40 hover:bg-slate-800 text-white rounded-lg font-medium text-xs transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        );
    };

    if (!isopen) return null;

    return (
        <Dialog
            open={isopen}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: isDarkMode
                            ? 'rgba(0, 0, 0, 0.75)'
                            : 'rgba(15, 23, 42, 0.55)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                    }
                }
            }}
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                    backgroundColor: isDarkMode ? '#0b0b14' : '#ffffff',
                    backgroundImage: isDarkMode
                        ? 'linear-gradient(135deg, #0b0b14 0%, #0d0d18 50%, #0b0b14 100%)'
                        : 'none',
                    boxShadow: isDarkMode
                        ? '0 25px 70px -15px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(99, 102, 241, 0.35), 0 0 60px rgba(79, 70, 229, 0.25)'
                        : '0 20px 50px -15px rgba(99, 43, 232, 0.3)',
                    overflow: 'hidden',
                    border: isDarkMode ? '1px solid rgba(99, 102, 241, 0.35)' : 'none',
                }
            }}
        >
            <DialogTitle className="relative p-0 m-0">
                <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl px-5 py-4">
                    <button
                        onClick={handleCloseBulk}
                        className="absolute top-2.5 right-2.5 w-7 h-7 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center transition-colors"
                    >
                        <IoIosCloseCircle size={16} className="text-white" />
                    </button>

                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-white/15 rounded-xl">
                            <FiUploadCloud className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold leading-tight">Bulk Order Upload</h2>
                            <p className="text-purple-100 text-xs leading-tight">Upload a CSV to create multiple orders</p>
                        </div>
                    </div>
                </div>
            </DialogTitle>

            <DialogContent
                sx={{
                    backgroundColor: isDarkMode ? '#0b0b14' : '#ffffff',
                    padding: '16px !important',
                    '&::-webkit-scrollbar': { width: '5px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: isDarkMode ? 'rgba(99, 102, 241, 0.5)' : '#c4b5fd',
                        borderRadius: '3px'
                    },
                }}
            >
                <form onSubmit={handleSubmit} className="space-y-3">
                    {/* Upload Zone */}
                    <label
                        htmlFor="dropzone-file"
                        className={`group relative flex items-center gap-3 w-full px-4 py-3.5 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${file
                            ? 'border-emerald-300 bg-emerald-50/60 dark:border-emerald-400/60 dark:bg-emerald-500/[0.08]'
                            : isDarkMode
                                ? 'border-indigo-400/60 bg-indigo-500/[0.06] hover:bg-indigo-500/10 hover:border-indigo-300'
                                : 'border-purple-200 bg-purple-50/40 hover:bg-purple-50 hover:border-purple-400'
                            }`}
                    >
                        <motion.div
                            animate={file ? { scale: [1, 1.1, 1] } : {}}
                            transition={{ duration: 0.35 }}
                            className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${file
                                ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 dark:shadow-[0_0_15px_rgba(16,185,129,0.6)]'
                                : 'bg-gradient-to-br from-indigo-500 to-purple-600 dark:shadow-[0_0_15px_rgba(99,102,241,0.6)]'
                                }`}
                        >
                            {file ? (
                                <CheckCircle className="w-4.5 h-4.5 text-white" />
                            ) : (
                                <FiUploadCloud className="w-4.5 h-4.5 text-white" />
                            )}
                        </motion.div>

                        <div className="min-w-0">
                            <p className={`text-sm font-semibold ${file ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-white'}`}>
                                {file ? 'File selected' : 'Drop CSV here or click to browse'}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-indigo-200/70">CSV only • Max 10MB</p>
                        </div>

                        <input
                            id="dropzone-file"
                            type="file"
                            className="hidden"
                            accept=".csv"
                            onChange={handleFileChange}
                        />
                    </label>

                    {/* File Info Section */}
                    <AnimatePresence>
                        {file && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="space-y-2 overflow-hidden"
                            >
                                <div className="flex items-center gap-2.5 bg-gray-50 dark:bg-indigo-500/[0.08] dark:border dark:border-indigo-500/30 rounded-xl px-3 py-2 border border-gray-100">
                                    <FileText className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-700 dark:text-white truncate">{file.name}</p>
                                        <p className="text-[11px] text-gray-400 dark:text-indigo-200/70">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                    </div>
                                    <span className="w-1.5 h-1.5 bg-emerald-500 dark:bg-emerald-400 rounded-full animate-pulse shrink-0 dark:shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                                </div>

                                {csvParsed && previewData.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handlePreviewClick}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 dark:shadow-lg dark:shadow-indigo-900/40 text-white rounded-xl font-medium text-sm transition-colors"
                                    >
                                        <Eye className="w-4 h-4" />
                                        Preview Data
                                        <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-[11px]">{previewData.length} rows</span>
                                    </button>
                                )}

                                {!csvParsed && (
                                    <div className="flex items-center justify-center gap-2 py-2.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200/70 dark:border-amber-400/40 rounded-xl">
                                        <div className="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                                        <span className="text-amber-700 dark:text-amber-300 text-xs font-medium">Parsing CSV file...</span>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={!file || loading}
                        className={`w-full px-4 py-3 font-semibold text-sm rounded-xl transition-all ${!file || loading
                            ? isDarkMode
                                ? 'bg-indigo-500/10 text-indigo-300/40 border border-indigo-500/25 cursor-not-allowed'
                                : 'bg-gray-300 text-white cursor-not-allowed'
                            : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 dark:shadow-lg dark:shadow-indigo-900/50 text-white active:scale-[0.98]'
                            }`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <Spinner />
                                Processing...
                            </span>
                        ) : (
                            'Create Bulk Orders'
                        )}
                    </button>
                </form>

                {/* Sample Download Section */}
                <div className="mt-3 flex items-center justify-between px-3.5 py-2.5 bg-purple-50/60 dark:bg-indigo-500/[0.08] dark:border dark:border-indigo-500/30 rounded-xl border border-purple-100">
                    <p className="text-xs text-gray-500 dark:text-indigo-200/80">Need a template?</p>
                    <a
                        href="/sampleFile/Sample.csv"
                        download="Sample.csv"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:text-indigo-700 dark:hover:text-indigo-200"
                    >
                        <FiDownload className="w-3.5 h-3.5" />
                        Download Sample
                    </a>
                </div>

                {/* Modals */}
                <ConfirmBulkOrder
                    isopen={isPopupOpen}
                    onClose={handleClose}
                    data={reqBody}
                    file={file}
                />
                {showPreview && <PreviewModal />}
            </DialogContent>
        </Dialog>
    );
};
export default UploadBulk;